import cn from 'classnames';
import { useRouter } from 'next/navigation';
import React, { useState, useEffect } from 'react';
import { getCurrency } from '@/app/currency';
import styles from '@entities/notes-card/notes-card-content.module.css';
import { useAddToBasketMutation, useGetBasketQuery } from '@shared/api';
import { ENV } from '@shared/config';
import type { Locale } from '@shared/libs';
import type { NotesCardQueryData } from '@shared/types';
import { Button, ButtonSize } from '@shared/ui';

type Props = {
  data: NotesCardQueryData['data'];
  locale: Locale;
  h1: string;
};

export const BuyForm = ({ locale, data, h1 }: Props) => {
  const [selectedPrice, setSelectedPrice] = useState<number>(data?.price?.MIDI?.VALUE || 0);
  const [selectedVariant, setSelectedVariant] = useState('MIDI');
  const router = useRouter();
  const [isBuy, setIsBuy] = useState(false);
  const { data: basket } = useGetBasketQuery();
  const mutation = useAddToBasketMutation();

  const { isfree, price, id } = data;

  useEffect(() => {
    if (basket) {
      const curNote = basket.data.find((item) => item.id === id);

      if (curNote) {
        setIsBuy(true);
        setSelectedVariant(curNote.PRICE.VARIANT);
      }
    }
  }, [basket, id]);

  const handleBuyClick = async () => {
    if (isBuy) {
      router.push('/personal/cart/');
    } else {
      const { data: dataResponse } = await mutation.mutateAsync({
        price: selectedPrice,
        variant: selectedVariant,
        offer_id: id,
      });

      // eslint-disable-next-line @typescript-eslint/await-thenable
      const currency = await getCurrency();

      if (!isfree) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        window.dataLayer.push({
          ecommerce: {
            currencyCode: currency?.value || ENV.DEFAULT_CERRENCY,
            add: {
              products: [
                {
                  id: id.toString(),
                  name: `${data.musicians?.[0]?.title} - ${data.title} - ${data.arrangement.title}`,
                  category: data?.instruments?.[0]?.title,
                  brand: data.arrangement.title,
                  price: data.price[selectedVariant as unknown as 'MIDI'].VALUE,
                  variant: selectedVariant,
                  quantity: 1,
                },
              ],
            },
          },
        });
      }

      if (dataResponse.REDIRECT) {
        fetch(`${ENV.STORAGE}${dataResponse.REDIRECT}`)
          .then((response) => response.blob())
          .then((blob) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${h1}.pdf`;
            link.target = '_blank';
            link.click();
          })
          .catch((error) => console.error('Error downloading file:', error));
      }
    }
  };

  return (
    <div className={styles.order}>
      <div className={styles.orderTitle}>
        {isfree ? locale.CT_BCE_CATALOG_FB_NOTES : locale.CT_BCE_CATALOG_FORMATS2}
      </div>
      {isfree ? (
        <span className={styles.orderDesc}>{locale.NOTE_FREE_FORMAT_TEXT}</span>
      ) : (
        <span className={styles.orderDesc}>{locale.NOTE_NO_FREE_FORMAT_TEXT}</span>
      )}
      {!isfree && price && (
        <div className={styles.formats}>
          {price.MIDI?.VALUE && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              className={styles.formatBlock}
              onClick={() => {
                setSelectedPrice(price.MIDI?.VALUE || 0);
                setSelectedVariant('MIDI');
              }}
            >
              <div className={styles.formRadio}>
                <input
                  id="radio-1"
                  type="radio"
                  name="price"
                  value={price.MIDI.VALUE}
                  checked={selectedPrice === price.MIDI?.VALUE}
                  onChange={() => {}}
                />
                <label htmlFor="radio-1">.pdf, .midi</label>
              </div>

              <span className={styles.formatPrice}>{price.MIDI?.PRINT_VALUE}</span>
            </div>
          )}
          {price.BASE?.VALUE && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              className={styles.formatBlock}
              onClick={() => {
                setSelectedPrice(price.BASE?.VALUE || 0);
                setSelectedVariant('BASE');
              }}
            >
              <div className={styles.formRadio}>
                <input
                  id="radio-2"
                  type="radio"
                  name="price"
                  value={price.BASE.VALUE}
                  checked={selectedPrice === price.BASE?.VALUE}
                  onChange={() => {}}
                />
                <label htmlFor="radio-2">.pdf</label>
              </div>
              <span className={styles.formatPrice}>{price.BASE?.PRINT_VALUE}</span>
            </div>
          )}
        </div>
      )}

      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <Button size={ButtonSize.L} onClick={handleBuyClick} loading={mutation.isLoading}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {isfree
          ? locale.NOTE_FREE_BTN
          : isBuy
            ? locale.NOTE_TO_BASKET_BTN
            : locale.NOTE_NO_FREE_BTN}
      </Button>

      {!isfree && (
        <div className={styles.orderInfo}>
          <div className={styles.infoBlock}>
            <div className={styles.guarantees}>{locale.NOTE_WARRANTY}</div>
            <div className={styles.pdfIncluded}>{locale.NOTE_INCLUDING_PDF}</div>
          </div>
          <span
            className={cn(styles.payments, {
              [styles.paymentsRu]: ENV.LANG === 'ru',
              [styles.paymentsCom]: ENV.LANG !== 'ru',
            })}
          />
        </div>
      )}
    </div>
  );
};
